import { ApiService, type ApiRequestConfig } from "./api.service";
import type {
  TJobRole,
  TCreateJobRole,
  TUpdateJobRole,
  TJobRoleMember,
  TJobRoleRecommendation,
  TJobRoleSummary,
  TSmartAssignResult,
} from "@/types/job-role";

export class WorkspaceJobRoleService extends ApiService {
  async list(slug: string): Promise<TJobRole[]> {
    return this.get(`/api/workspaces/${slug}/job-roles/`);
  }

  /** Job-role choices (id, name, colour, status) for the work-item pickers,
   *  readable by anyone who can create or edit work (NG-2099). */
  async listOptions(slug: string): Promise<TJobRoleSummary[]> {
    return this.get(`/api/workspaces/${slug}/job-role-options/`);
  }

  async retrieve(slug: string, id: string): Promise<TJobRole> {
    return this.get(`/api/workspaces/${slug}/job-roles/${id}/`);
  }

  async create(slug: string, data: TCreateJobRole): Promise<TJobRole> {
    return this.post(`/api/workspaces/${slug}/job-roles/`, data);
  }

  async update(slug: string, id: string, data: TUpdateJobRole): Promise<TJobRole> {
    return this.patch(`/api/workspaces/${slug}/job-roles/${id}/`, data);
  }

  /** Eligible workspace members (guests excluded) with an `assigned` flag for
   *  who currently holds this role — for the editor's member multiselect. */
  async members(slug: string, id: string): Promise<TJobRoleMember[]> {
    return this.get(`/api/workspaces/${slug}/job-roles/${id}/members/`);
  }

  /** Set the role's complete member set (admin bulk-assign). */
  async setMembers(
    slug: string,
    id: string,
    memberIds: string[],
  ): Promise<{ member_ids: string[] }> {
    return this.put(`/api/workspaces/${slug}/job-roles/${id}/members/`, { member_ids: memberIds });
  }

  async remove(slug: string, id: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/job-roles/${id}/`);
  }

  /** The current user's own membership (carries their job_role_id + role), for
   *  the self-service preferences picker. */
  async getMyMembership(slug: string): Promise<TMyMembership> {
    return this.get(`/api/workspaces/${slug}/workspace-members/me/`);
  }

  /** Set (or clear) the current user's own job role via the "me" endpoint. */
  async setMyJobRole(slug: string, jobRoleId: string | null): Promise<TMyMembership> {
    return this.patch(`/api/workspaces/${slug}/workspace-members/me/`, { job_role_id: jobRoleId });
  }

  /** Smart Assignment recommendations for every job role with work on a project. */
  async recommendations(
    slug: string,
    projectId: string,
    config?: ApiRequestConfig,
  ): Promise<TJobRoleRecommendation[]> {
    const res = await this.get<{ recommendations: TJobRoleRecommendation[] }>(
      `/api/workspaces/${slug}/projects/${projectId}/job-role-recommendations/`,
      config,
    );
    return res.recommendations;
  }

  /** Bulk-assign all of a job role's project work to one user (server resolves
   *  the work-item set). */
  async smartAssign(
    slug: string,
    projectId: string,
    jobRoleId: string,
    assigneeId: string,
  ): Promise<TSmartAssignResult> {
    return this.post(
      `/api/workspaces/${slug}/projects/${projectId}/job-roles/${jobRoleId}/smart-assign/`,
      { assignee_id: assigneeId },
    );
  }
}

export type TMyMembership = {
  role: string;
  job_role_id: string | null;
};
